import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeRaw from "rehype-raw";
import rehypeStringify from "rehype-stringify";
import juice from "juice";

// global Word console
export async function insertText(text) {
  try {
    await Word.run(async (context) => {
      let body = context.document.body;

      // Split the glossary text by new lines (\n)
      const lines = text.split("\n");

      let formattedText = `<div style="font-weight: bold; font-size: 30px; text-align:center;">Glossary</div><br><br><br>`;
      lines.forEach((line) => {
        let [boldText, descText] = line.split(":");

        if (boldText && descText) {
          // Format the text with bold for the first part and regular for the second
          formattedText += `<span style="font-weight: bold; font-size: 15px">${boldText}:</span> ${descText}<br>`;
        }
      });

      // Insert the formatted text with line breaks
      body.insertHtml(formattedText, Word.InsertLocation.end);
      await context.sync();
    });
  } catch (error) {
    console.log("Error inserting glossary text: " + error);
  }
}

export async function getDocumentName() {
  return new Promise((resolve, reject) => {
    Office.onReady((info) => {
      if (info.host === Office.HostType.Word) {
        const documentUrl = Office.context.document.url;

        if (documentUrl) {
          const matchResult = documentUrl.match(/[\\\/]([^\\\/]+)(?=\.docx$)/);

          if (matchResult) {
            resolve(matchResult[1]);
          } else {
            console.warn("The URL does not match the expected format.");
            resolve(null);
          }
        } else {
          console.warn("Document URL is not available.");
          resolve(null);
        }
      } else {
        reject("Not running in Word host");
      }
    });
  });
}

export async function getHtmlBetweenSections(sectionsArray, currentIndex) {
  return Word.run(async (context) => {
    let section1 = sectionsArray[currentIndex]?.section_name || "";
    section1 = section1.replace(/[\n\r\t\f\v\u2028\u2029]+/g, "").trim();

    const body = context.document.body;

    const result1 = body.search(section1, {
      matchCase: false,
      matchWholeWord: false,
      ignorePunct: true,
      ignoreSpace: true,
    });
    result1.load("items");

    await context.sync();

    if (result1.items.length === 0) {
      console.warn(`Section 1 "${section1}" not found`);
      return "";
    }

    const section1End = result1.items[0].getRange("End");
    let endRange = body.getRange("End"); // Default fallback
    let foundNextSection = false;

    for (let j = currentIndex + 1; j < sectionsArray.length; j++) {
      let sectionNext = sectionsArray[j]?.section_name || "";
      sectionNext = sectionNext.replace(/[\n\r\t\f\v\u2028\u2029]+/g, "").trim();
      if (!sectionNext) continue;

      const resultNext = body.search(sectionNext, {
        matchCase: false,
        matchWholeWord: false,
        ignorePunct: true,
        ignoreSpace: true,
      });
      resultNext.load("items");

      await context.sync();

      if (resultNext.items.length === 0) {
        continue; // section not found — try next
      }

      const startOfNext = resultNext.items[0].getRange("Start");
      startOfNext.load("start"); // Ensure startOfNext is loaded
      await context.sync(); // Sync after loading startOfNext

      endRange = startOfNext;
      break;
    }

    const range = section1End.expandTo(endRange);
    range.load(["html", "text"]);
    await context.sync();

    if (!range.text || range.text.trim() === "") {
      console.warn("⚠️ No content found between sections.");
      return "";
    }

    return range.getHtml() || "";
  }).catch((error) => {
    console.error("Error in getHtmlBetweenSections:", error);
    return "";
  });
}

// Replace HTML content between two section headings
export async function replaceHtmlBetweenSections(sectionsArray, currentIndex, htmlToInsert) {
  const section1 = sectionsArray[currentIndex]?.section_name?.replace(/[\n\r\t\f\v\u2028\u2029]+/g, "").trim() || "";

  return Word.run(async (context) => {
    const body = context.document.body;

    const result1 = body.search(section1, {
      matchCase: false,
      matchWholeWord: false,
      ignorePunct: true,
      ignoreSpace: true,
    });
    result1.load("items");

    await context.sync();

    if (result1.items.length === 0) {
      console.warn(`Section 1 "${section1}" not found`);
      return;
    }

    const startRange = result1.items[0].getRange("End");
    let endRange = body.getRange("End");

    // Search for the next found section starting from currentIndex + 1
    for (let j = currentIndex + 1; j < sectionsArray.length; j++) {
      const sectionNext = sectionsArray[j]?.section_name?.replace(/[\n\r\t\f\v\u2028\u2029]+/g, "").trim();
      if (!sectionNext) continue;

      const nextSearch = body.search(sectionNext, {
        matchCase: false,
        matchWholeWord: false,
        ignorePunct: true,
        ignoreSpace: true,
      });
      nextSearch.load("items");

      await context.sync();

      if (nextSearch.items.length === 0) {
        continue; // section not found — try next
      }

      if (nextSearch.items.length > 0) {
        endRange = nextSearch.items[0].getRange("Start");
        break;
      }
    }

    const rangeToReplace = startRange.expandTo(endRange);
    rangeToReplace.clear();
    startRange.insertHtml(`<p>${htmlToInsert}</p><br>`, Word.InsertLocation.start);

    await context.sync();
    // console.log("HTML inserted between sections.");
  }).catch((error) => {
    console.error("Error in replaceHtmlBetweenSections:", error);
  });
}

export async function extractProtocolNumber(filename) {
  let result = null;

  // 1) Standard format: XXX-XXX-XXXXX (e.g. 405-201-00188)
  const standardMatch = filename.match(/\b[A-Z0-9]{3}-\d{3}-\d{5}\b/i);
  if (standardMatch) {
    return standardMatch[0].trim();
  }

  // 2) Generalized exception pattern (e.g. IRL757C003, ABC123X005)
  const exceptionRe = /\b[A-Z]{2,5}\d{3,4}[A-Z]\d{3}\b/i;
  const exceptionMatch = filename.match(exceptionRe);
  if (exceptionMatch) {
    return exceptionMatch[0].trim();
  }

  // 3) Fallbacks — try more specific patterns first to avoid partial matches
  let m = null;

  // 3a) Token containing hyphen immediately before Protocol
  //    catches cases like "405-201-00188_Protocol" -> "405-201-00188"
  m = filename.match(/\b([A-Z0-9]+(?:-[A-Z0-9]+)+)[ _-]*Protocol\b/i);
  if (m && m[1]) {
    return m[1].trim();
  }

  // 3b) Token immediately after Protocol e.g. "Protocol_XYZ" -> "XYZ"
  m = filename.match(/\bProtocol[ _-]*([A-Z0-9-]+)\b/i);
  if (m && m[1]) {
    return m[1].trim();
  }

  // 3c) Token before Protocol that is not purely numeric (prefer letters or mixed)
  m = filename.match(/\b([A-Z][A-Z0-9-_]{1,})[ _-]*Protocol\b/i);
  if (m && m[1]) {
    return m[1].trim();
  }

  // 4) Nothing matched — return filename without extension
  result = filename.replace(/\.[^/.]+$/, "").trim();
  return result;
}

export async function getLatestDateFormatted() {
  const today = new Date();

  const day = String(today.getDate()).padStart(2, "0"); // → 01, 02, etc.
  const month = today.toLocaleString("en-US", { month: "short" }); // → Jan, Feb, Mar...
  const year = today.getFullYear(); // → 2025

  return `${day} ${month} ${year}`;
}

export async function insertTemplateIntoWord(sections, protocolId) {
  return Word.run(async (context) => {
    try {
      if (!sections) {
        // || sections.length === 0) {
        console.log("No content to insert. The API response was empty.");
        return;
      }

      // sections.forEach(async (section) => {
      const range = context.document.getSelection();

      const sectionName = sections?.section_name?.trim() || "";
      const content = sections?.generatedText?.trim() || "";

      // Regex patterns
      const multiLevelNumber = /^\d+\.\d+/; // e.g., 1.1, 1.2.1, etc.
      const singleLevelNumber = /^\d+\.\s/; // e.g., 1. , 2.
      const startsWithDigit = /^\d/.test(sectionName); // starts with a digit

      range.insertText("\n", Word.InsertLocation.end);

      const titleRange = range.insertText(sectionName + "\n", Word.InsertLocation.end);

      if (!startsWithDigit) {
        // e.g., "Research summary"
        titleRange.font.name = "Arial";
        titleRange.font.size = 14;
        titleRange.font.color = "black";
        titleRange.font.bold = true;
      } else if (multiLevelNumber.test(sectionName)) {
        // e.g., "1.1 Research summary", "2.1.3 Research summary"
        titleRange.font.name = "Arial";
        titleRange.font.size = 12;
        titleRange.font.color = "black";
        titleRange.font.bold = true;
      } else if (singleLevelNumber.test(sectionName)) {
        // e.g., "1. Research summary"
        titleRange.font.name = "Arial";
        titleRange.font.size = 14;
        titleRange.font.color = "black";
        titleRange.font.bold = true;
      } else {
        // fallback
        titleRange.font.name = "Arial";
        titleRange.font.size = 14;
        titleRange.font.color = "black";
        titleRange.font.bold = true;
      }

      // Insert Generated Text
      if (content) {
        const isHtml = /<\/?[a-z][\s\S]*>/i.test(content.trim());

        if (isHtml) {
          const inserted = range.insertHtml(`${content}<br><br>`, Word.InsertLocation.end);
          inserted.font.name = "Times New Roman";
          inserted.font.size = 12;

          const newLine = range.insertText("\n", Word.InsertLocation.end);
          newLine.select(Word.SelectionMode.start);
        } else {
          const textRange = range.insertText(content + "\n\n", Word.InsertLocation.end);
          textRange.font.name = "Times New Roman";
          textRange.font.size = 12;
          textRange.font.bold = false;

          const newLine = range.insertText("\n", Word.InsertLocation.end);
          newLine.select(Word.SelectionMode.start);
        }

        // Always move cursor to the new end
        const endRange = context.document.body.getRange(Word.RangeLocation.end);
        endRange.select();

        const protocolNumber = protocolId;
        const protocolText = protocolNumber || "___________";
        const latestDate = await getLatestDateFormatted();

        const footerHtml = `<html><head><style><!--
 /* Font Definitions */
 @font-face {font-family:"Cambria Math"; panose-1:2 4 5 3 5 4 6 3 2 4;}
 @font-face {font-family:Aptos;}
 /* Style Definitions */
 p.MsoNormal, li.MsoNormal, div.MsoNormal {
   margin-top:0in;
   margin-right:0in;
   margin-bottom:8.0pt;
   margin-left:0in;
   line-height:115%;
   font-size:12.0pt;
   font-family:"Times New Roman",serif;
 }
 p.MsoFooter, li.MsoFooter, div.MsoFooter {
   mso-style-link:"Footer Char";
   margin:0in;
   font-size:12.0pt;
   font-family:"Times New Roman",serif;
 }
 span.FooterChar {mso-style-name:"Footer Char"; mso-style-link:Footer;}
 .MsoChpDefault {font-size:12.0pt;}
 .MsoPapDefault {margin-bottom:8.0pt; line-height:115%;}
 @page WordSection1 {size:8.5in 11.0in; margin:1.0in 1.0in 1.0in 1.0in;}
 div.WordSection1 {page:WordSection1;}
--></style></head>
<body lang=EN-US style='word-wrap:break-word'>
<br />
<div class=WordSection1>
<table style="width:100%; border-collapse:collapse; font-family:'Times New Roman',serif; font-size:12pt;">
  <tr>
    <td style="text-align:left; vertical-align:top;">
      <p class=MsoFooter>Protocol #: ${protocolText}</p>
      <p class=MsoFooter>ICF Version #: 1.0</p>
      <p class=MsoFooter>ICF Date: ${latestDate}</p>
    </td>
    <td style="text-align:right; vertical-align:top;">
      <p class=MsoFooter>Participant Initials_______________</p>
      <p class=MsoFooter style="white-space:pre-wrap;">  ${"\n"}  </p>
      <p class=MsoFooter>Date______________________</p>
    </td>
  </tr>
</table>
<p class=MsoNormal>&nbsp;</p>
</div>
</body></html>`;
        insertHeaderFooterWithPageNumber(footerHtml);
      }
      // range.select(Word.SelectionMode.end);
      // });
      await context.sync();
    } catch (error) {
      console.error("Failed to insert fetched content into the document. Please try again:", error);
    }
  });
}

export async function insertHeaderFooterWithPageNumber(footerHtml) {
  await Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();

    if (sections.items.length === 0) {
      console.error("No sections found in the document.");
      return;
    }

    const section = sections.items[0];
    const header = section.getHeader("primary");
    const footer = section.getFooter("primary");

    // Clear existing header/footer
    header.clear();
    footer.clear();

    // === HEADER ===
    const headerParagraph = header.insertParagraph("", Word.InsertLocation.start);
    headerParagraph.alignment = "Right";

    // Insert HTML with Word field codes for Page X of Y
    // NOTE: This relies on Word's internal field syntax that auto-updates
    const pageNumberHtml = `
      <p style="text-align: right; font-family: 'Times New Roman'; font-size: 11pt;">
        Page <span style='mso-field-code:"PAGE"'></span> of <span style='mso-field-code:"NUMPAGES"'></span>
      </p>
    `;

    header.insertHtml(pageNumberHtml, Word.InsertLocation.replace);

    // === FOOTER ===
    footer.insertHtml(footerHtml, Word.InsertLocation.start);

    await context.sync();
    // console.log("Header (Page X of Y) and HTML footer inserted successfully.");
  }).catch((error) => {
    console.error("Error inserting header/footer:", error);
  });
}

function prepareHtmlForWord(html) {
  if (!html) return "";

  // 1. Convert CSS → inline (MAIN FIX)
  let inlined = juice(html);

  // 2. Remove unsupported tags for Word
  inlined = inlined
    .replace(/<head[\s\S]*?>[\s\S]*?<\/head>/gi, "")
    .replace(/<\/?html[^>]*>/gi, "")
    .replace(/<\/?body[^>]*>/gi, "");

  return inlined;
}

export async function insertTemplateIntoWordPLPS(sections, protocolId, euctNumber, instructionText = "") {
  return Word.run(async (context) => {
    try {
      if (!sections) {
        //|| sections.length === 0) {
        console.log("No content to insert. The API response was empty.");
        return;
      }

      // Always start at the end of the document
      let range = context.document.body.getRange(Word.RangeLocation.end);
      // insertInstructionText(instructionText)
      //add static text on top
      if (sections?.section_name === "Full Trial Title") {
        await insertTitlePLPS();
        await context.sync();

        // Move insertion point to end after header
        range = context.document.body.getRange(Word.RangeLocation.end);
      }
      range.insertText("\n", Word.InsertLocation.end);

      const inlineSections = ["Full Trial Title", "Plain Language Title", "EU Trial No.", "Otsuka Study ID"];

      // Insert each section
      // sections.forEach((section) => {
      const sectionName = sections?.section_name?.trim() || "";
      const content = sections?.generatedText?.trim() || "";
      const templateHtml = sections?.template?.trim() || "";

      // Section name (bold)
      if (inlineSections.includes(sectionName)) {
        const labelRange = range.insertText(`${sectionName}: `, Word.InsertLocation.end);
        labelRange.font.name = "Times New Roman";
        labelRange.font.size = 12;
        labelRange.font.bold = true;
      } else {
        const styledTitle = getStyledSectionTitle(sectionName);
        const isHtmlTitle = /<\/?[a-z][\s\S]*>/i.test(styledTitle);

        if (isHtmlTitle) {
          const safeTitleHtml = prepareEscapedHtmlForWord(styledTitle);
          range.insertHtml(safeTitleHtml, Word.InsertLocation.end);
        } else {
          const titleRange = range.insertText(styledTitle + "\n", Word.InsertLocation.end);
          titleRange.font.name = "Times New Roman";
          titleRange.font.size = 12;
          titleRange.font.bold = true;
        }
      }

      if (templateHtml) {
        const isHtmlTemplate = /<\/?[a-z][\s\S]*>/i.test(templateHtml);

        if (isHtmlTemplate) {
          const safeTemplateHtml = prepareEscapedHtmlForWord(templateHtml);
          console.log("isHtmlTemplate: ", safeTemplateHtml)

          range.insertHtml(
            `<div style="margin-bottom:8px;">${safeTemplateHtml}</div><br>`,
            Word.InsertLocation.end
          );
        } else {
          const tempRange = range.insertText(
            templateHtml + "\n\n",
            Word.InsertLocation.end
          );

          tempRange.font.name = "Times New Roman";
          tempRange.font.size = 12;
        }
      }

      // Content (check for HTML or plain text)
      if (content) {
        const isHtml = /<\/?[a-z][\s\S]*>/i.test(content);
        if (isHtml) {
          const safeHtml = prepareHtmlForWord(content);

          const insertedHtml = range.insertHtml(safeHtml, Word.InsertLocation.end);
          insertedHtml.font.name = "Times New Roman";
          insertedHtml.font.size = 12;
          insertedHtml.font.bold = false;
        } else {
          const insertedText = range.insertText(content + "\n", Word.InsertLocation.end);
          insertedText.font.name = "Times New Roman";
          insertedText.font.size = 12;
          insertedText.font.bold = false;
        }
        // range.insertText("\n", Word.InsertLocation.end);
      }
      // });
      // Always move cursor to the new end
      const endRange = context.document.body.getRange(Word.RangeLocation.end);
      endRange.select();

      // Add footer once
      const protocolNumber = protocolId
      // const protocolText = "" // protocolNumber || "XXX-XX-XXXX";
      insertHeaderPLPS(euctNumber);
      insertFooterWithPageNumberPLPS(protocolNumber);

      await context.sync();
      return endRange;
    } catch (error) {
      console.error("Failed to insert fetched content into the document. Please try again:", error);
    }
  });
}

export async function insertTitlePLPS() {
  return Word.run(async (context) => {
    const range = context.document.getSelection();

    const staticHtml = `
      <p style="
          font-family: Arial, sans-serif;
          font-size: 14pt;
          font-weight: bold;
          text-decoration: underline;
          margin: 0 0 6px 0;">
        Plain Language Protocol Synopsis
      </p>
      <div style="border-top: 2px solid black; margin-top: 6px;"></div>
    `;

    const insertedRange = range.insertHtml(staticHtml, Word.InsertLocation.end);
    insertedRange.insertText("\n", Word.InsertLocation.end);
    await context.sync();

    // console.log("Static Text inserted successfully.");
    return endRange;
  }).catch((error) => {
    console.error("Error inserting Static Text:", error);
  });
}

export async function insertHeaderPLPS(euctNumber) {
  return Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    if (sections.items.length === 0) return;
    const section = sections.items[0];

    const header = section.getHeader("primary");
    header.clear();
    await context.sync();

    const headerOoxml = `
      <pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
        <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">
          <pkg:xmlData>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
              <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
            </Relationships>
          </pkg:xmlData>
        </pkg:part>
        <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
          <pkg:xmlData>
            <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
                        xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing">
              <w:body>
                <w:p>
                  <w:pPr>
                    <w:pStyle w:val="Normal"/>
                    <w:rPr>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                  </w:pPr>
                  <w:r>
                    <w:rPr>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                    <w:t xml:space="preserve">EUCT Number </w:t>
                  </w:r>
                  <w:r>
                    <w:rPr>
                      <w:color w:val="00B050"/>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                    <w:t>${euctNumber}</w:t>
                  </w:r>
                  <w:r>
                    <w:rPr>
                      <w:noProof/>
                    </w:rPr>
                    <w:drawing>
                      <wp:anchor distT="0" distB="0" distL="114300" distR="0" simplePos="0"
                                 relativeHeight="251659264" behindDoc="0" locked="0"
                                 layoutInCell="1" allowOverlap="1">
                        <wp:simplePos x="0" y="0"/>
                        <wp:positionH relativeFrom="margin">
                          <wp:align>right</wp:align>
                        </wp:positionH>
                        <wp:positionV relativeFrom="paragraph">
                          <wp:posOffset>0</wp:posOffset>
                        </wp:positionV>
                        <wp:extent cx="2743200" cy="228600"/>
                        <wp:effectExtent l="0" t="0" r="0" b="0"/>
                        <wp:wrapNone/>
                        <wp:docPr id="1" name="PLPSTextBox"/>
                        <wp:cNvGraphicFramePr/>
                        <a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
                          <a:graphicData uri="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">
                            <wps:wsp xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">
                              <wps:cNvSpPr txBox="1"/>
                              <wps:spPr>
                                <a:xfrm>
                                  <a:off x="0" y="0"/>
                                  <a:ext cx="2743200" cy="228600"/>
                                </a:xfrm>
                                <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
                                <a:noFill/>
                                <a:ln><a:noFill/></a:ln>
                              </wps:spPr>
                              <wps:txbx>
                                <w:txbxContent>
                                  <w:p>
                                    <w:pPr><w:jc w:val="right"/></w:pPr>
                                    <w:r>
                                      <w:rPr>
                                        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                                        <w:sz w:val="20"/>
                                        <w:szCs w:val="20"/>
                                      </w:rPr>
                                      <w:t>Plain-language Protocol Synopsis</w:t>
                                    </w:r>
                                  </w:p>
                                </w:txbxContent>
                              </wps:txbx>
                              <wps:bodyPr wrap="square" lIns="0" tIns="0" rIns="0" bIns="0" anchor="t">
                                <a:noAutofit/>
                              </wps:bodyPr>
                            </wps:wsp>
                          </a:graphicData>
                        </a:graphic>
                      </wp:anchor>
                    </w:drawing>
                  </w:r>
                </w:p>
              </w:body>
            </w:document>
          </pkg:xmlData>
        </pkg:part>
      </pkg:package>
    `;

    header.insertOoxml(headerOoxml, Word.InsertLocation.end);
    await context.sync();
  });
}

export async function insertFooterWithPageNumberPLPS(protocolText) {
  await Word.run(async (context) => {
    const sections = context.document.sections;
    sections.load("items");
    await context.sync();
    if (sections.items.length === 0) return;
    const section = sections.items[0];

    const footer = section.getFooter("primary");
    footer.clear();
    await context.sync();

    const footerOoxml = `
      <pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
        <pkg:part pkg:name="/_rels/.rels" pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">
          <pkg:xmlData>
            <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
              <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
            </Relationships>
          </pkg:xmlData>
        </pkg:part>
        <pkg:part pkg:name="/word/document.xml" pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
          <pkg:xmlData>
            <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"
                        xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing">
              <w:body>

                <!-- Line 1: centered page number -->
                <w:p>
                  <w:pPr>
                    <w:pStyle w:val="Normal"/>
                    <w:jc w:val="center"/>
                    <w:rPr>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                  </w:pPr>
                  <w:fldSimple w:instr=" PAGE ">
                    <w:r>
                      <w:rPr>
                        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                        <w:sz w:val="20"/>
                        <w:szCs w:val="20"/>
                      </w:rPr>
                      <w:t>1</w:t>
                    </w:r>
                  </w:fldSimple>
                </w:p>

                <!-- Line 2: left = Protocol text, right = Amendment/Approval (floating box anchored to margin) -->
                <w:p>
                  <w:pPr>
                    <w:pStyle w:val="Normal"/>
                    <w:rPr>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                  </w:pPr>
                  <w:r>
                    <w:rPr>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                    <w:t xml:space="preserve">Protocol </w:t>
                  </w:r>
                  <w:r>
                    <w:rPr>
                      <w:color w:val="00B050"/>
                      <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                      <w:sz w:val="20"/>
                      <w:szCs w:val="20"/>
                    </w:rPr>
                    <w:t>${protocolText}</w:t>
                  </w:r>
                  <w:r>
                    <w:rPr>
                      <w:noProof/>
                    </w:rPr>
                    <w:drawing>
                      <wp:anchor distT="0" distB="0" distL="114300" distR="0" simplePos="0"
                                 relativeHeight="251659265" behindDoc="0" locked="0"
                                 layoutInCell="1" allowOverlap="1">
                        <wp:simplePos x="0" y="0"/>
                        <wp:positionH relativeFrom="margin">
                          <wp:align>right</wp:align>
                        </wp:positionH>
                        <wp:positionV relativeFrom="paragraph">
                          <wp:posOffset>0</wp:posOffset>
                        </wp:positionV>
                        <wp:extent cx="3200400" cy="228600"/>
                        <wp:effectExtent l="0" t="0" r="0" b="0"/>
                        <wp:wrapNone/>
                        <wp:docPr id="2" name="FooterInfoTextBox"/>
                        <wp:cNvGraphicFramePr/>
                        <a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
                          <a:graphicData uri="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">
                            <wps:wsp xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">
                              <wps:cNvSpPr txBox="1"/>
                              <wps:spPr>
                                <a:xfrm>
                                  <a:off x="0" y="0"/>
                                  <a:ext cx="3200400" cy="228600"/>
                                </a:xfrm>
                                <a:prstGeom prst="rect"><a:avLst/></a:prstGeom>
                                <a:noFill/>
                                <a:ln><a:noFill/></a:ln>
                              </wps:spPr>
                              <wps:txbx>
                                <w:txbxContent>
                                  <w:p>
                                    <w:pPr><w:jc w:val="right"/></w:pPr>
                                    <w:r>
                                      <w:rPr>
                                        <w:color w:val="00B050"/>
                                        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                                        <w:sz w:val="20"/>
                                        <w:szCs w:val="20"/>
                                      </w:rPr>
                                      <w:t xml:space="preserve">[Amendment X] </w:t>
                                    </w:r>
                                    <w:r>
                                      <w:rPr>
                                        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                                        <w:sz w:val="20"/>
                                        <w:szCs w:val="20"/>
                                      </w:rPr>
                                      <w:t xml:space="preserve">Approval: </w:t>
                                    </w:r>
                                    <w:r>
                                      <w:rPr>
                                        <w:color w:val="00B050"/>
                                        <w:rFonts w:ascii="Times New Roman" w:hAnsi="Times New Roman"/>
                                        <w:sz w:val="20"/>
                                        <w:szCs w:val="20"/>
                                      </w:rPr>
                                      <w:t>DD Mon YYYY</w:t>
                                    </w:r>
                                  </w:p>
                                </w:txbxContent>
                              </wps:txbx>
                              <wps:bodyPr wrap="square" lIns="0" tIns="0" rIns="0" bIns="0" anchor="t">
                                <a:noAutofit/>
                              </wps:bodyPr>
                            </wps:wsp>
                          </a:graphicData>
                        </a:graphic>
                      </wp:anchor>
                    </w:drawing>
                  </w:r>
                </w:p>

              </w:body>
            </w:document>
          </pkg:xmlData>
        </pkg:part>
      </pkg:package>
    `;

    footer.insertOoxml(footerOoxml, Word.InsertLocation.end);
    await context.sync();
  });
}

function resizeImages() {
  return (tree) => {
    // Loop through the tree nodes
    for (const node of tree.children || []) {
      // Check if the current node is an image element
      if (node.type === "element" && node.tagName === "img") {
        const src = node.properties?.src;

        // Only resize base64 images (optional check for type)
        if (typeof src === "string" && src.startsWith("data:image")) {
          // Set width and height directly as attributes (636px width, 408px height)
          node.properties.width = 620;
          node.properties.height = 408;
        }
      }

      // Loop through the children of the current node, if any
      for (const child of node.children || []) {
        // Check if the child is an image and apply resizing
        if (child.type === "element" && child.tagName === "img") {
          const src = child.properties?.src;

          if (typeof src === "string" && src.startsWith("data:image")) {
            // Set width and height directly as attributes (636px width, 408px height)
            child.properties.width = 620;
            child.properties.height = 408;
          }
        }
      }
    }
  };
}

export async function insertTextIntoWord(summary) {
  summary = {
    content_type_list: [],
    ...summary,
  };
  console.log(summary);

  return Word.run(async (context) => {
    try {
      const selection = context.document.getSelection();
      const findDocumentIndex = summary?.content_type_list?.filter((e, i) => e === "Document")?.map((_, i) => i) || [];

      const inputDataString =
        summary?.input_data
          ?.map((item, i) => {
            if (findDocumentIndex?.includes(i) || !summary?.content_type_list?.length) {
              return "";
            } else {
              return `<div>${item}</div><br>`;
            }
          })
          ?.join("<br>") || "";

      const imgData =
        summary?.images
          ?.map((e) => {
            const imgdesc = e?.imgdesc || "";
            const imgpath = e?.imgpath || "";
            return `<div>
                    <h5 style="font-size: 12px;">${imgdesc}</h5>
                    </div>
                    <div style="background-color: #f5f5f5; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
                    <img
                      src="data:image/jpeg;base64,${imgpath}"
                      alt="Rendered Image"
                      style="width: 100%; max-width: 600px;"
                    />
                    </div>
                    `;
          })
          ?.join("<br>") || "";

      // const imgData =
      // summary?.Images
      //   ?.map((item, i) => {
      //     if () {
      //       return "";
      //     } else {
      //       return `<div>${item}</div><br>`;
      //     }
      //   })
      //   ?.join("<br>") || "";

      // Convert Markdown to HTML manually
      let processedMarkdown = "";
      if (summary?.generatedText?.includes("<!DOCTYPE html>")) {
        processedMarkdown = summary?.generatedText;
      } else {
        let markdownContent = summary?.generatedText?.replace(/<\/table>/g, "</table>\n\n") || "";
        processedMarkdown = await unified()
          .use(remarkParse) // Parse Markdown
          .use(remarkGfm) // Support GitHub-Flavored Markdown (tables, checkboxes, etc.)
          .use(remarkRehype, { allowDangerousHtml: true }) // Convert Markdown to HTML tree, preserving HTML
          .use(rehypeRaw) // Allow raw HTML inside Markdown
          .use(rehypeStringify)
          .use(resizeImages) // Convert the HTML tree to a string
          .process(markdownContent);
      }

      const soaPrefixHtml = summary?.isSoa
        ? `<div style="font-size: 16px; font-weight: bold; font-family: 'Calibri', sans-serif;">
            ${summary?.table_name || ""} 
          </div>`
        : "";
      const footnotesdata = summary?.footnotes ? `<br>${summary?.footnotes}<br>` : "";

      selection.insertHtml(
        `${inputDataString}
        ${soaPrefixHtml}
        <div class="generatedText" style="font-size: 16px; color: #000000; font-family: 'Calibri', sans-serif;">
          ${processedMarkdown}
        </div>
         ${footnotesdata}
        <br>
        ${imgData}
        `,
        // ${summary?.generatedText?.replace(/\n/g, "<br>")}
        // <img src="data:image/jpeg;base64,${byteData}" style="width: 100%; max-width: 600px;" />`,
        Word.InsertLocation.replace
      );
      selection.select(Word.SelectionMode.end);
      await context.sync();
    } catch (error) {
      console.error("Error generating summary:", error);
      if (error instanceof OfficeExtension.Error) {
        console.error("Debug info:", JSON.stringify(error.debugInfo));
      }
    }
  });
}

// Function to read the selected content of the Word document using Office.js
// export async function initializeOffice() {
//   Office.onReady(function (info) {
//     if (info.host === Office.HostType.Word) {
//       document.getElementById("getSelectedText").onclick = async function () {
//         try {
//           await Word.run(async function (context) {
//             var selection = context.document.getSelection();
//             context.load(selection);
//             await context.sync();
//             console.log("Selected Text: " + selection.text);
//           });
//         } catch (error) {
//           console.log("Error: " + error);
//         }
//       };
//     }
//   });
// }

// Function to read the content of the Word document using Office.js

export async function readWordContent() {
  return Word.run(async (context) => {
    // Get the entire body content of the document
    const body = context.document.body;
    // Load the text from the body of the document
    body.load("text");
    // Synchronize the context to populate the body text
    await context.sync();
    // console.log("Word Content:", body.text);
    return body.text;
  }).catch(function (error) {
    console.log("Error reading Word document content: " + error.message);
  });
}

export async function insertGeneratedTextWithSectionIntoWord(insertText) {
  return Word.run(async (context) => {
    try {
      const range = context.document.getSelection();
      range.insertHtml(insertText, Word.InsertLocation.end);
      range.select(Word.SelectionMode.end);

      await context.sync();
    } catch (error) {
      console.error("Failed to insert fetched content into the document. Please try again:", error);
    }
  });
}

export const handleFindAndReplace = async (findText, replaceText) => {
  await Word.run(async (context) => {
    // Search for the text in the Word document's body
    const searchResults = context.document.body.search(findText, {
      matchCase: false,
      matchWholeWord: true,
    });

    // Load the search results so we can access them
    context.load(searchResults);
    await context.sync();

    // If results are found, replace them with the replace text
    if (searchResults.items.length > 0) {
      searchResults.items.forEach((item) => {
        // Insert the replacement text
        item.insertHtml(replaceText, Word.InsertLocation.replace);
      });
      await context.sync();
    } else {
    }
  });
};

export const handleFindSearchCheck = (findText) => {
  return new Promise((resolve, reject) => {
    Word.run(async (context) => {
      // Search for the text in the Word document's body
      const searchResults = context.document.body.search(findText, {
        matchCase: false,
        matchWholeWord: true,
      });

      context.load(searchResults);
      await context.sync();

      if (searchResults.items.length > 0) {
        resolve(true); // Found the text, resolve as true
      } else {
        resolve(false); // Not found, resolve as false
      }
    }).catch((error) => {
      reject(error); // Reject the promise if an error occurs
    });
  });
};

export const replaceTextInWord = async (generatedText, summarizedText) => {
  try {
    await Word.run(async (context) => {
      const body = context.document.body;

      // Load the text property of the document body
      body.load("text");
      await context.sync();

      const bodyText = body.text;

      // Check if the generatedText exists in the document
      if (!bodyText.includes(generatedText)) {
        console.log("Generated text not found in the document.");
        return;
      }

      // Replace the generated text with the summarized text
      const updatedText = bodyText.replaceAll(generatedText, summarizedText);

      // Clear the document body and insert the updated text
      body.clear(); // Clear the existing content
      body.insertText(updatedText, Word.InsertLocation.start); // Insert the updated content

      await context.sync();
      console.log("Text replaced successfully in the document.");
    });
  } catch (error) {
    console.error("Error replacing text in the Word document:", error);
  }
};

export const getSectionContext = async () => {
  try {
    return await Word.run(async (context) => {
      const body = context.document.body;
      // Get the HTML content directly
      const htmlContent = body.getHtml();
      await context.sync();
      console.log(htmlContent.value);
    });
  } catch (error) {
    console.error("Error reading document content as HTML:", error);
    throw error;
  }
};

function prepareEscapedHtmlForWord(html = "") {
  if (!html) return "";

  let decoded = html;
  try {
    decoded = JSON.parse(
      `"${html.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`
    );
  } catch (e) { }

  const txt = document.createElement("textarea");
  txt.innerHTML = decoded;
  decoded = txt.value;

  const inlined = juice(decoded);

  const parser = new DOMParser();
  const fixedDoc = parser.parseFromString(inlined, "text/html");
  const body = fixedDoc.body;

  const INHERITABLE = ["color", "font-style", "font-family", "font-size", "font-weight"];

  body.querySelectorAll("ul, ol").forEach((list) => {
    const listStyle = list.style;

    list.querySelectorAll("li").forEach((li) => {
      // Force Word to treat it as a list item
      li.style.display = "list-item";
      li.style.marginLeft = "30px";

      INHERITABLE.forEach((prop) => {
        const parentVal = listStyle[prop];
        if (parentVal && !li.style[prop]) {
          li.style[prop] = parentVal;
        }
      });

      // Push styles to inline children
      li.querySelectorAll("*").forEach((child) => {
        INHERITABLE.forEach((prop) => {
          const parentVal = listStyle[prop];
          if (parentVal && !child.style[prop]) {
            child.style[prop] = parentVal;
          }
        });
      });
    });
  });

  return body.innerHTML.trim();
}

export async function insertInstructionText(instructionText = "") {
  if (!instructionText) return;

  return Word.run(async (context) => {
    const body = context.document.body;

    body.load("text");
    await context.sync();

    // Insert only once
    if (body.text.includes("Plain-language Protocol Synopsis")) {
      return;
    }

    const safeHtml = prepareEscapedHtmlForWord(instructionText);

    body.insertHtml(
      `
      <div style="text-align:center; font-family: Arial, Helvetica, sans-serif; font-size:14pt; font-weight:bold; margin-bottom:12px;">
        Plain-language Protocol Synopsis
      </div>

      ${safeHtml}
      <p>&nbsp;</p>
      `,
      Word.InsertLocation.start
    );

    await context.sync();
  });
}

function getStyledSectionTitle(sectionName) {
  const normalized = sectionName?.replace(/\s+/g, " ").trim();

  if (normalized === "What is/are the Treatment(s) being Tested in the Trial?") {
    return `<p style="font-family:'Times New Roman',serif; font-size:12pt; font-weight:bold; color:#000000; margin:12pt 0;">What <span style="color:#00B050; mso-color-alt:#00B050;">is/are</span> the Treatment<span style="color:#00B050; mso-color-alt:#00B050;">(s)</span> being Tested in the Trial?</p><br/>`.trim();
  }

  return sectionName;
}